import styled from '@emotion/styled';
import { ElementType, ReactNode, useEffect, useRef, useState } from 'react';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { theme } from '../../theme';
import { BodyMedium } from '../Typography';

const DropdownFooter = styled.div`
  border-top: ${theme.spacing.a} solid ${theme.colors.borderMain.default};
  padding: ${theme.spacing.g} 0 ${theme.spacing.f} 0;
`;

const Menu = styled.div`
  padding: ${theme.spacing.f} 0 ${theme.spacing.g} 0;
`;

const PrimaryActionContainer = styled.div`
  padding: 0 ${theme.spacing.g};
`;

const StyledDropdownButton = styled(DropdownButton)<{ show: boolean; isLogged: boolean }>`
  & > div {
    border-radius: ${theme.spacing.d};
  }
  && .btn-primary {
    color: ${theme.colors.actionPrimary.default} !important;
    border: ${theme.spacing.a} solid ${theme.colors.actionPrimary.default} !important;
    border-radius: 50% !important;
    background-color: white !important;
    width: 30px;
    height: 30px;
    padding: unset !important;
    font-size: ${theme.typography.body.small.button.fontSize};
    font-style: normal;
    font-weight: ${theme.typography.body.small.button.fontWeight};
    line-height: ${theme.typography.body.small.button.lineHeight};
    letter-spacing: ${theme.typography.body.small.button.letterSpacing};
    text-align: center;

    &:hover {
      border: ${theme.spacing.a} solid ${theme.colors.actionPrimary.default} !important;
      border-radius: 50% !important;
    }
    &:focus,
    &:active {
      border: ${theme.spacing.a} solid ${theme.colors.actionPrimary.default} !important;
      border-radius: 50% !important;
      box-shadow: none !important;
      color: ${theme.colors.actionPrimary.default} !important;
      background-color: white !important;
    }

    &::after {
      margin-top: 9px;
      transform: rotate(0deg);
      transition-duration: 0.5s;
      transition-property: transform;

      ${({ show }) =>
        show &&
        `
        transform: rotate(180deg);
        transition-duration: 0.5s;
        transition-property: transform;
      `}
      ${({ isLogged }) =>
        isLogged &&
        `
         display: none !important;
      `}
    }
  }
`;

const MenuHeader = styled.div`
  font-family: Roboto, Arial, Helvetica, sans-serif;
  font-style: normal;
  font-weight: normal !important;
  font-size: ${theme.typography.body.small.regular.fontSize} !important;
  line-height: ${theme.typography.body.small.regular.lineHeight} !important;
  padding: ${theme.spacing.f} 0 ${theme.spacing.g} 0;
  border-bottom: ${theme.spacing.a} solid ${theme.colors.borderMain.default};
  margin-bottom: ${theme.spacing.d};
`;

const MenuButtons = styled.div`
  font-family: Roboto;
  font-style: normal;
  font-weight: ${theme.typography.body.medium.regular.fontWeight} !important;
  font-size: ${theme.typography.body.medium.regular.fontSize} !important;
  line-height: ${theme.typography.body.medium.regular.lineHeight} !important;
`;

const DropdownItem = styled(Dropdown.Item)`
  cursor: pointer;
  &:active {
    background-color: ${theme.colors.actionPrimary.default};
  }
`;

export type MenuItem = {
  title: string;
  element: ElementType<any>;
  onClick?: () => void;
  href?: string;
  dataIcom?: string;
};

export type MenuItems = MenuItem[];

interface IProps {
  buttonTitle: (string | null)[];
  title: string;
  classname?: string;
  showMenuHeader: boolean;
  menuItems: MenuItems;
  showFooter: boolean;
  showPrimaryActionInFooter: boolean;
  primaryAction: ReactNode;
  footerMenuItem: MenuItem;
  isLogged: boolean;
}

export const UserMenuDropdown = ({
  title,
  buttonTitle,
  classname,
  showMenuHeader,
  menuItems,
  showFooter,
  showPrimaryActionInFooter,
  primaryAction,
  footerMenuItem,
  isLogged,
}: IProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<any>(null);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current?.contains(event.target as Node)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <StyledDropdownButton
      title={isLogged ? buttonTitle : undefined}
      className={classname}
      show={showDropdown}
      onToggle={toggleDropdown}
      ref={dropdownRef}
      data-icom={'user-menu'}
      isLogged={isLogged}
      alignRight={true}
    >
      {showMenuHeader && (
        <MenuHeader>
          <DropdownItem disabled={true} title={title}>
            <BodyMedium bold color={theme.colors.text.secondary} data-icom={'user-menu-username'}>
              {title.length > 20 ? title.substr(0, 20) + '...' : title}
            </BodyMedium>
          </DropdownItem>
        </MenuHeader>
      )}
      <MenuButtons>
        <Menu>
          {menuItems.map((item) => {
            if (item) {
              return (
                <DropdownItem
                  as={item.element}
                  key={item.title}
                  onClick={item.onClick}
                  href={item.href}
                  data-icom={item.dataIcom}
                >
                  {item.title}
                </DropdownItem>
              );
            } else {
              return null;
            }
          })}
        </Menu>
        {showFooter && (
          <DropdownFooter>
            {showPrimaryActionInFooter ? (
              <PrimaryActionContainer>{primaryAction}</PrimaryActionContainer>
            ) : (
              <DropdownItem
                as={footerMenuItem.element}
                onClick={footerMenuItem.onClick}
                href={footerMenuItem.href}
                data-icom={footerMenuItem.dataIcom}
              >
                {footerMenuItem.title}
              </DropdownItem>
            )}
          </DropdownFooter>
        )}
      </MenuButtons>
    </StyledDropdownButton>
  );
};
